import { useQuery } from '@tanstack/react-query';
import { addDays, format } from 'date-fns';

import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import {
  getOccupants,
  isReservation,
  ServiceReservation,
} from 'restaurantAdmin/reservations/service/apiHelpers';
import { useServiceSearchParams } from './useServiceSearchParams';
import { DATE_PARAM_FORMAT } from '../../../utils/constants';
import { ReservationStatus } from '@shared/types/reservations';

export const START_OF_DAY = '00:00:00';
export const END_OF_RESTAURANT_DAY = '04:00:00';

interface UseOccupantsQueryProps {
  omitStatuses?: ReservationStatus[];
}

export const useOccupantsQuery = ({
  omitStatuses = [],
}: UseOccupantsQueryProps = {}) => {
  const { id: restaurantId } = useRestaurant();
  const { date, occupantId, occupantType, guestSearch } =
    useServiceSearchParams();

  const startDate = format(date, DATE_PARAM_FORMAT);
  const endDate = format(addDays(date, 1), DATE_PARAM_FORMAT);

  return useQuery({
    queryKey: ['restaurant', restaurantId, 'occupants', startDate],
    queryFn: () => {
      return getOccupants({
        restaurantId: restaurantId,
        startDate,
        startTime: START_OF_DAY,
        endDate,
        endTime: END_OF_RESTAURANT_DAY,
        guestSearchQuery: guestSearch ? guestSearch : undefined,
        omitStatuses: omitStatuses,
      });
    },
    initialData: [],
    select: (occupants) => {
      return {
        occupants,
        selectedOccupantFromOccupants: occupants.find(
          (occupant) =>
            occupant.id === occupantId && occupant.type === occupantType,
        ),
        notableOccupants: occupants.filter((occupant) => {
          return (
            isReservation(occupant) &&
            (occupant.isVIP ||
              occupant.hasDietaryRestrictionOrAllergy ||
              Boolean(occupant.note))
          );
        }) as ServiceReservation[],
      };
    },
  });
};
